import React, { useCallback, useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { ACCEPTED_IMAGES, uploadImage, useAssetUrl } from "./assets";

const MAX_ATTACHMENTS = 4;

// Images jointes à un message (chat Nexus, commentaire) : enregistrées dans les images
// d'offre, elles voyagent sous forme de références « asset:ID » que l'agent peut regarder.
export const useImageAttachments = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = useCallback(async (files) => {
    const images = [...(files || [])].filter((file) => file.type?.startsWith("image/"));
    if (!images.length) return;
    setBusy(true);
    setError("");
    try {
      for (const file of images.slice(0, MAX_ATTACHMENTS)) {
        const ref = await uploadImage(file);
        setItems((current) => (current.some((item) => item.ref === ref) || current.length >= MAX_ATTACHMENTS
          ? current : [...current, { ref, name: file.name || "image" }]));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const remove = useCallback((ref) => setItems((current) => current.filter((item) => item.ref !== ref)), []);
  const clear = useCallback(() => { setItems([]); setError(""); }, []);

  // À brancher sur la zone de saisie : coller une capture ou y déposer une image.
  const inputHandlers = {
    onPaste: (event) => {
      const files = [...(event.clipboardData?.files || [])];
      if (files.some((file) => file.type?.startsWith("image/"))) {
        event.preventDefault();
        add(files);
      }
    },
    onDragOver: (event) => { if ([...(event.dataTransfer?.types || [])].includes("Files")) event.preventDefault(); },
    onDrop: (event) => {
      const files = [...(event.dataTransfer?.files || [])];
      if (files.length) {
        event.preventDefault();
        add(files);
      }
    },
  };

  return { items, busy, error, add, remove, clear, inputHandlers };
};

// Texte ajouté au message pour l'agent : il ouvre ces images avec offers__asset_view.
export const attachmentsNote = (items) => (items.length
  ? `\n\n[Images jointes : ${items.map((item) => `${item.ref} (${item.name})`).join(", ")}]`
  : "");

export const AttachButton = ({ attachments, className = "", label = "Joindre une image" }) => {
  const ref = useRef(null);
  return (
    <>
      <button type="button" title={label} aria-label={label} disabled={attachments.busy}
        onClick={() => ref.current?.click()}
        className={`rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 ${className}`}>
        {attachments.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
      </button>
      <input ref={ref} type="file" multiple accept={ACCEPTED_IMAGES.join(",")} className="hidden" aria-label={label}
        onChange={(event) => { attachments.add(event.target.files); event.target.value = ""; }} />
    </>
  );
};

const Thumb = ({ refId, name, onRemove, size = 48 }) => {
  const url = useAssetUrl(refId);
  return (
    <span className="relative inline-flex">
      {url
        ? <a href={url} target="_blank" rel="noreferrer" title={name}>
            <img src={url} alt={name} style={{ width: size, height: size }} className="rounded border border-slate-200 object-cover" />
          </a>
        : <span style={{ width: size, height: size }} className="inline-block animate-pulse rounded bg-slate-100" />}
      {onRemove && (
        <button type="button" onClick={() => onRemove(refId)} aria-label={`Retirer ${name}`}
          className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-700 p-0.5 text-white">
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

export const AttachmentThumbs = ({ items, onRemove, size }) => (items.length ? (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => <Thumb key={item.ref} refId={item.ref} name={item.name} onRemove={onRemove} size={size} />)}
  </div>
) : null);
